import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { LogoMark } from '../../logo/logo-mark';
import { useAuth } from '../../../context/auth-provider';
import { publicApi } from '../../../api/public-client';
import { resolvePostAuthRoute } from '../../../utils/post-auth-route';
import { AutoHeight, Collapse, Pop } from '../../../motion';
import {
  Shell,
  Card,
  BrandRow,
  Wordmark,
  Heading,
  Subheading,
  FullWidthButton,
  InfoBanner,
  SuccessBlock,
  SuccessIcon,
} from '../onboarding.styled';

type State = 'verifying' | 'success' | 'expired' | 'invalid';

export const VerifyEmailPage: React.FC = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user, onboarding, permissions, refreshAuth } = useAuth();
  const token = params.get('token');
  const [state, setState] = useState<State>('verifying');
  const ran = useRef(false); // guard React 18 StrictMode double-invoke

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    if (!token) {
      setState('invalid');

      return;
    }
    publicApi
      .post('/verify-email', { token })
      .then(() => {
        setState('success');

        // RE-READ THE SESSION. The server has just written email_verified_at,
        // and this provider's user object is a cache of GET /auth — without
        // this, the browser that did the verifying is the one place still
        // reporting the account as unverified, and the "Continue" button below
        // drops the user back onto a dashboard that is still nagging them to
        // verify. Deliberately not awaited before showing success: the
        // verification IS done at this point, and a slow /auth must not turn a
        // completed action into a spinner.
        //
        // Only for a session that exists. A signed-out visitor clicking the
        // link has nothing to refresh and goes to sign in.
        if (user) {
          refreshAuth().catch(() => {
            // A failed re-read leaves a stale-but-valid session; the next probe
            // or reload picks the change up. Never downgrade a successful
            // verification to an error because a follow-up read failed.
          });
        }
      })
      .catch(err => {
        setState(err?.response?.status === 410 ? 'expired' : 'invalid');
      });
    // `user`/`refreshAuth` are deliberately absent: this must run EXACTLY once
    // per token, and the ran-guard above is what enforces that.
    // eslint-disable-next-line
  }, [token]);

  const copy = {
    verifying: { title: 'Verifying your email…', sub: 'One moment.' },
    success: {
      title: 'Email verified',
      sub: 'Your email address is confirmed. You’re all set.',
    },
    expired: {
      title: 'This link has expired',
      sub: 'Sign in and resend the verification email from your dashboard.',
    },
    invalid: {
      title: 'We couldn’t verify this link',
      sub: 'It may have already been used. Sign in to continue.',
    },
  }[state];

  return (
    <Shell>
      <Card>
        <BrandRow>
          <LogoMark size={40} aria-hidden="true" />
          <Wordmark>Lobbero</Wordmark>
        </BrandRow>
        {/* The tick pops, the banner and the button unfold, and the card grows
            to fit — rather than all three landing in one commit under a
            heading that has just changed. */}
        <AutoHeight>
          <Heading>{copy.title}</Heading>
          <Subheading>{copy.sub}</Subheading>
          <Collapse open={state === 'success'}>
            <SuccessBlock>
              <Pop as="div">
                <SuccessIcon aria-hidden="true">✓</SuccessIcon>
              </Pop>
            </SuccessBlock>
            <InfoBanner tone="success">
              Thanks — that keeps your account secure.
            </InfoBanner>
          </Collapse>
          <Collapse open={state !== 'verifying'}>
            <FullWidthButton
              type="button"
              onClick={() =>
                navigate(
                  user
                    ? resolvePostAuthRoute(user, onboarding, permissions)
                    : '/'
                )
              }
            >
              {user ? 'Continue' : 'Go to sign in'}
            </FullWidthButton>
          </Collapse>
        </AutoHeight>
      </Card>
    </Shell>
  );
};
